import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Backdrop, Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Link, Typography, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TemplateWorksheets, { TemplateWorksheetRef } from './TemplateWorksheets';
import TemplateWorksheetColumns, { TemplateWorksheetColumnsRef } from './TemplateWorksheetColumns';
import { ITemplateWorksheetProps, ITemplateWorksheetColumnsProps, Template, IWorksheet, IWorksheetColumn, IAvailableWorksheetColumnsProps } from '../../types/types';
import AvailableWorksheetColumns from './AvailableWorksheetColumns';
import { FieldGroup } from '../../types/enums';
import { AlertType } from '../../types/enums';
import useTemplateGenerationPolling, { TemplateGenerationPollStatus } from '../../hooks/useTemplateGenerationPolling';
import { checkTemplateGenerationCompleteAsync } from './template.api';
import { TEMPLATE_REGENERATION_COMPLETE_MESSAGE, TEMPLATE_REGENERATION_FAILED_MESSAGE, TEMPLATE_REGENERATION_STATUS_CHECK_FAILED_MESSAGE, TEMPLATE_REGENERATION_TIMEOUT_MESSAGE } from '../../constants/CommonConstants';
import { showAlert } from '../Alerts/alerts.actions';
import { normalizeCustomName } from '../../utilities/utilities';
interface TemplateEditorProps {
    templateId: number;
    open: boolean;
    onClose: () => void;
}

const TemplateEditorDialog: React.FC<TemplateEditorProps> = ({ templateId, open, onClose }) => {
    const dispatch = useDispatch();
    const [isDirty, setIsDirty] = useState(false);
    const [template, setTemplate] = useState<Template | null>(null);
    const worksheetsRef = useRef<TemplateWorksheetRef>(null);
    const worksheetColumnsRef = useRef<TemplateWorksheetColumnsRef>(null);
    const availableColumnsRef = useRef<TemplateWorksheetColumnsRef>(null);
    const [performInitialFirstPageOpen, setPerformInitialFirstPageOpen] = useState(false);
    const [openFirstDialog, setOpenFirstDialog] = useState(false);
    const [openSecondDialog, setOpenSecondDialog] = useState(false);
    const [openThirdDialog, setOpenThirdDialog] = useState(false);
    const [worksheetProps, setWorksheetProps] = useState<ITemplateWorksheetProps | null>(null);
    const [worksheetColumnsProps, setWorksheetColumnsProps] = useState<ITemplateWorksheetColumnsProps | null>(null);
    const [availableWorksheetColumnsProps, setAvailableWorksheetColumnsProps] = useState<IAvailableWorksheetColumnsProps | null>(null);
    const [currentWorksheet, setCurrentWorksheet] = useState<IWorksheet | null>(null);
    const [lastSavedTemplate, setLastSavedTemplate] = useState<Template | null>(null);
    const [currentWorksheetId, setCurrentWorksheetId] = useState<number | null>(null);
    const [isWaitingForRegeneration, setIsWaitingForRegeneration] = useState(false);
    const [isAvailableColumnsLoading, setIsAvailableColumnsLoading] = useState(false);
    type RegenerationScreen = 'worksheets' | 'worksheetColumns' | 'availableColumns';
    const [regenerationBanner, setRegenerationBanner] = useState<{ screen: RegenerationScreen; severity: AlertType; message: string } | null>(null);
    const regenerationResolveRef = useRef<((status: TemplateGenerationPollStatus) => void) | null>(null);

    const currentScreen: RegenerationScreen | null = openFirstDialog
        ? 'worksheets'
        : openSecondDialog
            ? 'worksheetColumns'
            : openThirdDialog
                ? 'availableColumns'
                : null;

    const { startPolling: startRegenerationPolling } = useTemplateGenerationPolling({
        checkCompletion: checkTemplateGenerationCompleteAsync,
        onCompleted: () => regenerationResolveRef.current?.('completed'),
        onTimedOut: () => regenerationResolveRef.current?.('timedOut'),
        onError: () => regenerationResolveRef.current?.('failed'),
    });

    const waitForRegeneration = useCallback((savedTemplateId: number): Promise<TemplateGenerationPollStatus> => {
        return new Promise<TemplateGenerationPollStatus>((resolve) => {
            regenerationResolveRef.current = resolve;
            startRegenerationPolling(savedTemplateId);
        });
    }, [startRegenerationPolling]);

    const handleOpenThirdDialog = useCallback((currentWorksheetId: number, incomingTemplate: Template) => {

        setCurrentWorksheetId(currentWorksheetId);
        setIsAvailableColumnsLoading(true);

        setAvailableWorksheetColumnsProps(
            {
                incomingTemplate: incomingTemplate,
                lastSavedTemplate: lastSavedTemplate ?? incomingTemplate,
                currentWorksheetId: currentWorksheetId,
                onDirtyChange: setIsDirty,
                onLoadingChange: setIsAvailableColumnsLoading,
            });

        setRegenerationBanner(null);
        setOpenSecondDialog(false);
        setOpenThirdDialog(true);
    }, []);

    const handleOpenSecondDialog = useCallback((workSheetId: number, incomingTemplate: Template) => {

        if (!incomingTemplate)
            return;

        // Keep currentWorksheetId in sync so any later save-success handler (handleSave) that
        // refreshes props using currentWorksheetId! has a real, non-null id, rather than relying
        // solely on handleOpenThirdDialog to have set it (which never runs if the user goes
        // straight to/stays on this page and saves without visiting Add & Remove first).
        setCurrentWorksheetId(workSheetId);

        setWorksheetColumnsProps(
            {
                incomingTemplate: incomingTemplate,
                lastSavedTemplate: lastSavedTemplate ?? incomingTemplate,
                currentWorksheetId: workSheetId,
                onDirtyChange: setIsDirty,
                handleOpenThirdDialog: handleOpenThirdDialog
            });
        setRegenerationBanner(null);
        setOpenSecondDialog(true);
    }, [lastSavedTemplate, handleOpenThirdDialog])


    const handleOpenNextDialog = useCallback((workSheet: IWorksheet, incomingTemplate: Template) => {

        setCurrentWorksheet(workSheet);
        setOpenFirstDialog(false);

        if (workSheet.worksheetColumns?.every(column => column.fieldIsRequired) || workSheet.worksheetColumns?.length === 0) {
            handleOpenThirdDialog(workSheet.id, incomingTemplate);
            return;
        }

        handleOpenSecondDialog(workSheet.id, incomingTemplate);

    }, [handleOpenSecondDialog]);


    useEffect(() => {
        const fetchTemplate = async () => {
            try {
                const response = await fetch(`api/template/${templateId}`);

                if (!response.ok) {
                    throw new Error('Failed to fetch template');
                }

                const data = await response.json();
                // The backend has no CustomName column, so a freshly-fetched template's
                // worksheets/columns never include a customName property (undefined), while
                // edits made in this dialog always normalize it to an explicit null (see
                // normalizeCustomName). Deriving customName from name/codeName-vs-fieldName here
                // keeps the initial state consistent with that shape, so the isDirty
                // JSON.stringify comparisons don't see undefined-vs-null as a difference and
                // incorrectly report the template as dirty. This also correctly re-displays any
                // custom name applied in a prior session.
                const normalizedData = updateTemplateCustomNamesFromNames(data);
                setTemplate(normalizedData);
                setLastSavedTemplate(normalizedData);
                setPerformInitialFirstPageOpen(true);

            } catch (error) {
                console.error('Error fetching template:', error);
                dispatch(showAlert({
                    hidden: false,
                    severity: AlertType.error,
                    message: 'Failed to load template'
                }));
            }
        };

        if (templateId && open) {
            setRegenerationBanner(null);
            initializePages();
            fetchTemplate();
        }
    }, [templateId, open]);

    useEffect(() => {

        const handleInitialOpenFirstDialog = (initialTemplate: Template) => {
            initializePages();

            if (initialTemplate) {

                setWorksheetProps({
                    incomingTemplate: initialTemplate,
                    lastSavedTemplate: initialTemplate,
                    onDirtyChange: setIsDirty,
                    handleOpenNextDialog: handleOpenNextDialog
                });

                setRegenerationBanner(null);
                setPerformInitialFirstPageOpen(false); // Reset the flag after the first open
                setOpenFirstDialog(true);
            }

        }

        if (template && performInitialFirstPageOpen) {
            handleInitialOpenFirstDialog(template);
        }

    }, [template, performInitialFirstPageOpen, handleOpenNextDialog]);

    const initializePages = () => {
        setWorksheetProps(null);
        setWorksheetColumnsProps(null);
        setAvailableWorksheetColumnsProps(null);
        setOpenFirstDialog(false);
        setOpenSecondDialog(false);
        setOpenThirdDialog(false);
        setIsAvailableColumnsLoading(false);
    }

    const handleOpenFirstDialog = (template: Template | null) => {

        if (template) {

            setWorksheetProps({
                incomingTemplate: template,
                lastSavedTemplate: lastSavedTemplate ?? template,
                onDirtyChange: setIsDirty,
                handleOpenNextDialog: handleOpenNextDialog
            });
            setRegenerationBanner(null);
            setOpenFirstDialog(true);
        }

    }


    const handleRegenerationResult = (status: TemplateGenerationPollStatus, screen: RegenerationScreen): boolean => {
        setIsWaitingForRegeneration(false);

        if (status === 'timedOut') {
            dispatch(showAlert({
                hidden: false,
                severity: AlertType.warning,
                message: TEMPLATE_REGENERATION_TIMEOUT_MESSAGE
            }));
            setRegenerationBanner({ screen, severity: AlertType.warning, message: TEMPLATE_REGENERATION_TIMEOUT_MESSAGE });
            return false;
        }

        if (status === 'failed') {
            dispatch(showAlert({
                hidden: false,
                severity: AlertType.error,
                message: TEMPLATE_REGENERATION_STATUS_CHECK_FAILED_MESSAGE
            }));
            setRegenerationBanner({ screen, severity: AlertType.error, message: TEMPLATE_REGENERATION_STATUS_CHECK_FAILED_MESSAGE });
            return false;
        }

        setRegenerationBanner(null);
        dispatch(showAlert({
            hidden: false,
            severity: AlertType.success,
            message: TEMPLATE_REGENERATION_COMPLETE_MESSAGE
        }));
        return true;
    };

    const handleSave = async () => {

        setRegenerationBanner(null);
        setIsWaitingForRegeneration(true);

        if (worksheetsRef.current) {
            const updatedTemplateFromFirstPage = await worksheetsRef.current.getData();

            if (updatedTemplateFromFirstPage) {
                let savedTemplate;
                try {
                    savedTemplate = await saveTemplate(updatedTemplateFromFirstPage, 'worksheets');
                } catch (error) {
                    setIsWaitingForRegeneration(false);
                    return;
                }

                const status = await waitForRegeneration(savedTemplate.id);
                const shouldClose = handleRegenerationResult(status, 'worksheets');

                if (shouldClose) {
                    onClose();
                }
            }
            else {
                setIsWaitingForRegeneration(false);
            }

        }

        if (worksheetColumnsRef.current) {

            const updatedTemplateFromSecondPage = await worksheetColumnsRef.current.getCurrentTemplate();

            if (updatedTemplateFromSecondPage) {
                let newTemplate;
                try {
                    newTemplate = await saveTemplate(updatedTemplateFromSecondPage, 'worksheetColumns');
                } catch (error) {
                    setIsWaitingForRegeneration(false);
                    return;
                }

                // Refresh local state with the persisted template (real, non-zero WorksheetColumn
                // Ids) immediately after a successful save, before checking the regeneration poll
                // outcome. Otherwise a retried save after a timeout would resubmit rows with Id: 0,
                // causing duplicate inserts. Also refresh the top-level `template` state so any
                // subsequent navigation (e.g. performInitialFirstPageOpen re-triggering
                // handleInitialOpenFirstDialog) uses fresh data instead of a stale template that
                // doesn't yet reflect the newly added/changed columns.
                setTemplate(newTemplate);
                setWorksheetColumnsProps(
                    {
                        incomingTemplate: newTemplate,
                        lastSavedTemplate: newTemplate,
                        currentWorksheetId: currentWorksheetId!,
                        onDirtyChange: setIsDirty,
                        handleOpenThirdDialog: handleOpenThirdDialog
                    });

                const status = await waitForRegeneration(newTemplate.id);
                const proceed = handleRegenerationResult(status, 'worksheetColumns');

                if (!proceed) {
                    return;
                }

                setOpenSecondDialog(false);
                setPerformInitialFirstPageOpen(true);
            }
            else {
                setIsWaitingForRegeneration(false);
            }
        }

        if (availableColumnsRef.current) {

            const updatedTemplateFromThirdPage = await availableColumnsRef.current.getCurrentTemplate();

            if (updatedTemplateFromThirdPage) {
                let newTemplate;
                try {
                    newTemplate = await saveTemplate(updatedTemplateFromThirdPage, 'availableColumns');
                } catch (error) {
                    setIsWaitingForRegeneration(false);
                    return;
                }

                // Refresh both the worksheet-columns and available-columns props with the
                // persisted template (real, non-zero WorksheetColumn Ids) immediately after a
                // successful save, before checking the regeneration poll outcome. Otherwise a
                // retried save after a timeout would resubmit rows with Id: 0, causing duplicate
                // inserts (this dialog stays open when the poll times out). Also refresh the
                // top-level `template` state so it doesn't go stale relative to the props above.
                setTemplate(newTemplate);
                setWorksheetColumnsProps(
                    {
                        incomingTemplate: newTemplate,
                        lastSavedTemplate: newTemplate,
                        currentWorksheetId: currentWorksheetId!,
                        onDirtyChange: setIsDirty,
                        handleOpenThirdDialog: handleOpenThirdDialog
                    });

                setAvailableWorksheetColumnsProps(
                    {
                        incomingTemplate: newTemplate,
                        lastSavedTemplate: newTemplate,
                        currentWorksheetId: currentWorksheetId!,
                        onDirtyChange: setIsDirty,
                        onLoadingChange: setIsAvailableColumnsLoading,
                    });

                const status = await waitForRegeneration(newTemplate.id);
                const proceed = handleRegenerationResult(status, 'availableColumns');

                if (!proceed) {
                    return;
                }

                setOpenThirdDialog(false);
                setOpenSecondDialog(true);
            }
            else {
                setIsWaitingForRegeneration(false);
            }

        }
    };

    const handleCancel = async () => {

        if (worksheetsRef.current) {
            if (isDirty && !window.confirm('Discard changes?')) {
                return;
            }
            worksheetsRef.current.reset();
            onClose();
        }

        if (worksheetColumnsRef.current) {

            if (worksheetColumnsRef.current.reset()) {

                let returnedTemplate = worksheetColumnsRef.current.getCurrentOriginalTemplate();

                if (returnedTemplate) {
                    setTemplate(returnedTemplate);
                }

                handleOpenFirstDialog(returnedTemplate ?? template);
                setOpenSecondDialog(false);
            }
        }

        if (availableColumnsRef.current) {

            if (availableColumnsRef.current.reset()) {

                let returnedTemplate = availableColumnsRef.current.getCurrentOriginalTemplate();

                if (returnedTemplate) {
                    setTemplate(returnedTemplate);
                    setOpenThirdDialog(false);
                    if (currentWorksheetId) {
                        handleOpenSecondDialog(currentWorksheetId, returnedTemplate);
                    }
                }
            }
        }
    };

    const returnToTemplateView = () => {
        if (availableColumnsRef.current) {

            if (availableColumnsRef.current.reset()) {

                var returnedTemplate = availableColumnsRef.current.getCurrentOriginalTemplate();

                if (returnedTemplate) {
                    setTemplate(returnedTemplate);
                }

                setOpenThirdDialog(false);
                setOpenSecondDialog(false);
                handleOpenFirstDialog(returnedTemplate ?? template);
            }
        }

    }

    const handleClose = (_: unknown, reason: "backdropClick" | "escapeKeyDown") => {

        if (reason === 'backdropClick') {
            return;	// ignore backdrop click
        }

        if (isDirty) {
            if (window.confirm('Discard changes?')) {
                onClose();
            }
        }
        else {
            onClose();
        }
    };

    
    const updateTemplateForCustomNames = (template: Template) => {

        // Fall back to the true default name (fieldName/codeName) when customName is cleared,
        // not to column.name/worksheet.name - those still hold whatever custom name was persisted
        // from a prior save, so falling back to them silently kept the old custom name in the DB
        // even after clearing the Custom Name field and saving again (AB#1141705 follow-up).
        const updatedWorksheets = template.worksheets.map((worksheet) => {
            const updatedColumns = worksheet.worksheetColumns?.map((column) => ({
                ...column,
                name: normalizeCustomName(column.customName) ?? column.fieldName,
            }));

            return {
                ...worksheet,
                name: normalizeCustomName(worksheet.customName) ?? worksheet.codeName,
                worksheetColumns: updatedColumns,
            };
        });

        return {
            ...template,
            worksheets: updatedWorksheets,
        };

    };

    const updateTemplateCustomNamesFromNames = (template: Template) => {

        const updatedWorksheets = template.worksheets.map((worksheet) => {
            const updatedColumns = worksheet.worksheetColumns?.map((column) => ({
                ...column,
                customName: column.name !== column.fieldName ? column.name : null,
            }));

            return {
                ...worksheet,
                customName: worksheet.name !== worksheet.codeName ? worksheet.name : null,
                worksheetColumns: updatedColumns,
            };
        });

        return {
            ...template,
            worksheets: updatedWorksheets,
        };

    };

    const saveTemplate = async (template: Template, screen: RegenerationScreen) => {

        try {
            var updatedTemplate = updateTemplateForCustomNames(template);

            const response = await fetch('api/template/saveTemplate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedTemplate),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            let jsonResponse = await response.json();
            const savedTemplate = jsonResponse.template;

            var savedTemplateWithCustomNames = await updateTemplateCustomNamesFromNames(savedTemplate);
            setTemplate(savedTemplateWithCustomNames);
            setLastSavedTemplate(JSON.parse(JSON.stringify(savedTemplateWithCustomNames))); // Deep copy
            return savedTemplateWithCustomNames;
        } catch (error) {
            console.error('Error saving template:', error);
            dispatch(showAlert({
                hidden: false,
                severity: AlertType.error,
                message: TEMPLATE_REGENERATION_FAILED_MESSAGE
            }));
            setRegenerationBanner({ screen, severity: AlertType.error, message: TEMPLATE_REGENERATION_FAILED_MESSAGE });
            throw error;
        }
    }

    const isReady = !!(template && (openFirstDialog || openSecondDialog || openThirdDialog));

    return (
        <>
            <Backdrop open={open && !isReady} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Dialog
            open={open && isReady}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            disableEscapeKeyDown
            PaperProps={{ sx: { position: 'relative' } }}
        >
            <Backdrop
                open={isAvailableColumnsLoading}
                transitionDuration={0}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.modal + 1,
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            {template && worksheetProps && openFirstDialog && (
                <>
                    <DialogTitle sx={{ m: 0, p: 2 }}>
                        {template.name}
                        <IconButton
                            aria-label="close"
                            onClick={() => handleClose(null, 'escapeKeyDown')}
                            disabled={isWaitingForRegeneration}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent>
                        <TemplateWorksheets
                            templateWorksheetProps={worksheetProps}
                            ref={worksheetsRef}
                        />
                    </DialogContent>
                </>
            )}

            {worksheetColumnsProps && template && currentWorksheet && openSecondDialog && (
                <>
                    <DialogTitle sx={{ m: 0, p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Link component="button" variant="body2" onClick={handleCancel}>
                                <Typography>
                                    {template.name}
                                </Typography>
                            </Link>
                            <Typography sx={{ ml: 2 }}>
                                / {currentWorksheet.customName ?? currentWorksheet.name}
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={() => handleClose(null, 'escapeKeyDown')}
                            disabled={isWaitingForRegeneration}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent>
                        <TemplateWorksheetColumns
                            templateWorksheetColumnsProps={worksheetColumnsProps}
                            ref={worksheetColumnsRef}
                        />
                    </DialogContent>
                </>
            )}

            {availableWorksheetColumnsProps && template && openThirdDialog && currentWorksheet && (
                <>
                    <DialogTitle sx={{ m: 0, p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Link component="button" variant="body2" onClick={returnToTemplateView}>
                                <Typography>
                                    {template.name}
                                </Typography>
                            </Link>
                            <Link component="button" variant="body2" onClick={handleCancel}>
                            <Typography sx={{ ml: 2 }}>
                                / {currentWorksheet.customName ?? currentWorksheet.name}
                                </Typography>
                            </Link>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={() => handleClose(null, 'escapeKeyDown')}
                            disabled={isWaitingForRegeneration}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <AvailableWorksheetColumns
                            templateWorksheetColumnsProps={availableWorksheetColumnsProps!}
                            ref={availableColumnsRef}
                        />
                    </DialogContent>
                </>
            )}

            {regenerationBanner && regenerationBanner.screen === currentScreen && (
                <Box sx={{ px: 2, pt: 2 }}>
                    <Alert severity={regenerationBanner.severity} onClose={() => setRegenerationBanner(null)}>
                        {regenerationBanner.message}
                    </Alert>
                </Box>
            )}

            {isReady && (
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        disabled={isWaitingForRegeneration}
                    >
                        {!isDirty ? 'Close' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!isDirty || isWaitingForRegeneration || regenerationBanner?.severity === AlertType.error}
                        startIcon={isWaitingForRegeneration ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {isWaitingForRegeneration ? 'Regenerating...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
        </>
    );
};

export default TemplateEditorDialog;
